import { createFileRoute, Link } from "@tanstack/react-router";
import { WIKIMEDIA_CREDITS } from "@/data/media-credits";
import logoAsset from "@/assets/lifewestway-logo.png.asset.json";
import { makeSeoDescription } from "@/lib/seo-description";

const SITE = "https://lifewestway.com";
const TITLE = "Media Credits | LifeWestWay";
const DESC =
  "Full attribution for every photo used on LifeWestWay: photographer, licence, and a link to the original source file.";
const URL_ = `${SITE}/media-credits`;
const IMAGE = `${SITE}${logoAsset.url}`;

export const Route = createFileRoute("/media-credits")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: makeSeoDescription(DESC) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: makeSeoDescription(DESC) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_ },
      { property: "og:image", content: IMAGE },
      { property: "og:site_name", content: "LifeWestWay" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: makeSeoDescription(DESC) },
      { name: "twitter:image", content: IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://lifewestway.com/media-credits" }],
  }),
  component: MediaCreditsPage,
});

function MediaCreditsPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link title="Return to the LifeWestWay homepage" to="/" className="hover:text-accent">
          Home
        </Link>
        <span className="px-2">/</span>
        <span>Media Credits</span>
      </nav>

      <h1 className="mt-4 font-display text-4xl font-bold text-foreground sm:text-5xl">
        Image &amp; Media Credits
      </h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground/75">
        LifeWestWay publishes only text written by our own editorial team and imagery that is
        licensed for commercial reuse. We do not host or embed copyrighted photos, video, or
        third-party article text without permission.
      </p>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold text-accent">Where our media comes from</h2>
        <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/80">
          <li>
            <strong className="text-foreground">Wikimedia Commons</strong> — freely licensed photos
            (Creative Commons or public domain). Each file is credited individually below with its
            author and licence.
          </li>
          <li>
            <strong className="text-foreground">Unsplash</strong> — used under the Unsplash Licence,
            which permits free commercial use without attribution. We still choose location- and
            topic-specific photographs (the actual city, campus, or landmark an article discusses)
            rather than generic desk-and-laptop stock, and each photo is used only once across the
            site.
          </li>
          <li>
            <strong className="text-foreground">Flag icons</strong> — flagcdn.com and the
            MIT-licensed <span className="text-foreground">circle-flags</span> set. National flags
            are in the public domain.
          </li>
          <li>
            <strong className="text-foreground">Brand assets</strong> — the LifeWestWay logo and
            illustrations are original works owned by LifeWestWay.
          </li>
          <li>
            <strong className="text-foreground">Editorial text</strong> — written in-house. Official
            figures (visa fees, salary bands, tax thresholds) are summarised in our own words and
            linked to the issuing government source rather than copied.
          </li>
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">Photo attribution</h2>
        <p className="mt-2 text-sm text-foreground/70">
          {WIKIMEDIA_CREDITS.length} photographs sourced from Wikimedia Commons. Click a title to
          view the original file, its full licence terms, and the photographer's page.
        </p>

        <ul className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card/60">
          {WIKIMEDIA_CREDITS.map((c) => (
            <li key={c.url} className="px-4 py-3">
              <a title={`Open the original source for ${c.title} in a new tab`}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-foreground underline-offset-2 hover:text-accent hover:underline"
              >
                {c.title}
              </a>
              <p className="mt-1 text-xs text-muted-foreground">
                © {c.author} — licensed under {c.license}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-accent">Report a rights issue</h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground/80">
          If you own the rights to any material on this site and believe it has been used
          incorrectly, contact us and we will remove or correct the credit promptly.
        </p>
        <Link title="Contact LifeWestWay about media credits"
          to="/contact"
          className="mt-5 inline-flex items-center rounded-md bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/90"
        >
          Contact the editorial team
        </Link>
      </section>
    </div>
  );
}
